import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { FormGroupDirective, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  startWith,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { ComponentAbstract } from '@app/components/abstract/component.abstract';
import {
  DynamicTicketType,
  DynamicTicketTypeNotifyAssigneeMode,
  DynamicTicketTypeService,
} from '@app/services/dynamic-ticket-type.service';
import { TicketsService } from '@app/services/tickets.service';
import { loadUnitList } from '@dashboards/store/actions/dashboard.actions';
import { selectUnitList, selectUnitListLoading } from '@dashboards/store/selectors/dashboards.selectors';
import { selectSelectedPortfolioId } from '@dashboards/store/selectors/property-selector.selectors';
import { loadPropertyUsers } from '@main-application/administration/store/actions/administration.actions';
import {
  selectPropertyUsers,
  selectPropertyUsersLoading,
} from '@main-application/administration/store/selectors/administration.selectors';
import { allPortfoliosValue } from '@main-application/application-menu/property-selector/constants';
import { CompletedId } from '@main-application/inspections/components/inspection-add-dialog/custom-notifications-dialog/custom-notifications-dialog.component';
import {
  CustomNotifyId,
  CustomNotifyOption,
  DontNotifyId,
  DontNotifyOption,
} from '@main-application/inspections/components/inspection-add-dialog/inspection-add-regular-form/inspection-add-regular-form.component';
import { BuiltInTicketType } from '@main-application/management/pages/service-configuration/dynamic-tickets/components/add-ticket-type-dialog/add-ticket-type-dialog.component';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';
import { selectUserData } from '@main-application/store/selectors/user.selectors';
import { selectProperties, selectPropertiesLoading } from '@portfolio/store/portfolio.selectors';
import { GetRecurrencePeriodTypeList } from '@shared/constants/recurrence-period-type-list.const';
import { PriorityType } from '@shared/enums/priority-type';
import { getDateObjectFunction } from '@shared/functions/get-date-object.function';
import { getPortfolioProperties } from '@shared/functions/get-portfolio-properties.function';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestPropertyModel } from '@shared/interfaces/rest-portfolio-model.interface';
import { TenantData } from '@shared/interfaces/tenant-data';
import { AppointmentTypeEnum, RestBulkRepeatingTicket } from '@shared/interfaces/turnover.interface';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { isExist } from '@shared/utils/exist.util';
import { filterNullish$ } from '@shared/utils/rxjs/filter-nullish.rxjs.util';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import { BulkTicketData, CategoryOption, TopicOption } from '../add-ticket-modal/add-ticket-modal.component';
import { DialogResult } from '../config/dialog-result.enum';
import { TicketCustomNotificationDialogComponent } from './ticket-custom-notification-dialog/ticket-custom-notification-dialog.component';

@UntilDestroy()
@Component({
  selector: 'app-add-bulk-ticket-modal',
  templateUrl: './add-bulk-ticket-modal.component.html',
  styleUrls: ['../add-ticket-modal/add-ticket-modal.component.scss', './add-bulk-ticket-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class AddBulkTicketModalComponent extends ComponentAbstract implements OnInit, AfterViewInit {
  protected cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  public dialog = inject(MatDialog);
  public dialogRef: MatDialogRef<AddBulkTicketModalComponent> = inject(MatDialogRef);
  public data: BulkTicketData = inject(MAT_DIALOG_DATA);
  private formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);
  private store: Store<{}> = inject(Store);
  private timezoneService: TimezoneService = inject(TimezoneService);
  private ticketService: TicketsService = inject(TicketsService);
  private ticketTypeService: DynamicTicketTypeService = inject(DynamicTicketTypeService);
  protected snackbarService: SnackbarService = inject(SnackbarService);

  today = this.timezoneService.getCurrentDateOnly();
  form: UntypedFormGroup = this.formBuilder.group({
    dynamicTicketType: [null as number, [Validators.required]],
    id: [0],
    title: [null, [Validators.required, Validators.maxLength(255)]],
    description: [null, [Validators.maxLength(1000)]],
    assigneeId: [null],
    dueDate: [null, [Validators.required]],
    repeatUntil: [null],
    priority: [null, [Validators.required]],
    propertyId: [null as number, [Validators.required]],
    bulkUnitIds: [null as number[], [Validators.required]],
    recurringPeriod: [null as number],
    appointmentType: AppointmentTypeEnum.Unknown,
    accessNotes: [null, [Validators.maxLength(1000)]],
    ticketCategoryId: null,
    ticketTopicId: null,
    origin: null,

    notifyOnMode: [DontNotifyId],
    customNotifyOnMode: [DynamicTicketTypeNotifyAssigneeMode.OnDueDate as DynamicTicketTypeNotifyAssigneeMode],
    customNotifyOn: [null as number],
    remindOn: [null as number],
    thenRemindOn: [null as number],
    until: [null as number],
  });

  CustomNotifyId = CustomNotifyId;
  DontNotifyId = DontNotifyId;
  DynamicTicketTypeNotifyAssigneeMode = DynamicTicketTypeNotifyAssigneeMode;
  appointmentTypeOptions: IRadioButtonOption<AppointmentTypeEnum | string>[] = [
    { value: AppointmentTypeEnum.Unknown, label: 'Unknown' },

    { value: AppointmentTypeEnum.NotRequired, label: 'Not Required' },
    { value: AppointmentTypeEnum.Call, label: 'Call' },
    { value: AppointmentTypeEnum.PermissionGiven, label: 'Permission Given' },
    { value: AppointmentTypeEnum.PermissionNotGiven, label: 'Permission Not Given' },

    { value: AppointmentTypeEnum.DAY_BEFORE_NOTICE, label: 'Day before notice' },
    { value: AppointmentTypeEnum.BY_APPOINTMENT, label: 'By appointment' },
    { value: AppointmentTypeEnum.NO_PREFERENCE, label: 'No preference' },
  ];
  recurrenceOptions: IRadioButtonOption<number>[] = [];
  notifyOptions: IRadioButtonOption<number>[] = this.createNotifyOptions();
  isNotifyDialogOpen = false;
  dialogTimeout: ReturnType<typeof setTimeout>;

  users$ = new BehaviorSubject<RestUserModel[]>([]);
  assigneeName$ = combineLatest([this.users$, this.form.get('assigneeId')?.valueChanges.pipe(startWith(null))]).pipe(
    debounceTime(100),
    map(([users, assigneeId]) => {
      const id = assigneeId ?? this.form.get('assigneeId')?.value;
      return users?.find(user => user.id === id)?.displayName ?? 'Assignee';
    })
  );

  dynamicTicketTypesOpt: IRadioButtonOption<number>[] = [];
  dynamicTicketTypes: DynamicTicketType[] = [];
  isTicketTypesLoading = true;
  properties: IRadioButtonOption<number>[] = [];
  propertiesList: RestPropertyModel[] = [];
  units$ = new BehaviorSubject<IRadioButtonOption<number>[] | null>(null);
  selectPropertiesLoading$: Observable<boolean> = this.store.select(selectPropertiesLoading);
  unitListLoading$: Observable<boolean> = this.store.select(selectUnitListLoading);
  isEmptyPropertyInited = false;

  portfolioId!: number;
  loadingUnit$ = new BehaviorSubject<boolean>(false);
  updateInProgress$ = new BehaviorSubject(false);
  propertyUserListLoading$: Observable<boolean>;
  users: RestUserModel[] = [];
  declare tenantData: TenantData;
  ticket: RestBulkRepeatingTicket = null;
  formSubmitted = false;

  originOptions: IRadioButtonOption<string>[] = [];
  categories: CategoryOption[] = [];
  availableCategories: number[] = [];
  availableTopics: number[] = [];
  topics: TopicOption[] = [];
  selectedPropertyId: number;
  selectedTypeId: number;
  isWorkOrder = false;
  isSettingsLoading = false;
  isCategoriesLoading = false;
  isLoading = true;
  pmsType: number;
  defaultCategoryId: number;
  defaultTopicId: number;
  defaultOrigin: string;

  formInitialized = false;

  @ViewChild(FormGroupDirective) formGroupDirective: FormGroupDirective;

  constructor() {
    super(inject(ChangeDetectorRef));
  }

  ngOnInit(): void {
    this.initTicket();
  }

  ngAfterViewInit(): void {
    this.isLoading = true;
    this.cdr.detectChanges();
  }

  initTicket() {
    if (this.data.ticket) {
      this.ticket = this.data.ticket;
    } else if (this.data.ticketId) {
      this.ticketService
        .getBulkTicket(this.data.ticketId)
        .pipe(take(1))
        .subscribe(ticket => {
          this.ticket = ticket;
          this.initData();
        })
        .untilDestroyed(this);
      return;
    }

    this.initData();
  }

  initData() {
    this.getUnits();

    this.propertyUserListLoading$ = this.store.select(selectPropertyUsersLoading).pipe(shareReplay());
    this.store
      .select(selectPropertyUsers)
      .pipe(
        untilDestroyed(this),
        filter(users => !!users),
        tap(users => {
          this.users$.next(users);
          this.cdr.detectChanges();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    combineLatest([
      this.getDynamicTicketTypes(),
      this.getProperties(),
      this.store.select(selectUserData).pipe(filterNullish$(), take(1)),
    ])
      .pipe(delay(10), take(1))
      .subscribe(([dynamicTicketTypes, properties, userData]) => {
        this.dynamicTicketTypesOpt = dynamicTicketTypes;
        this.properties = properties;
        this.userData = userData;
        this.cdr.detectChanges();

        this.initFormSubscriptions();
      })
      .untilDestroyed(this);

    this.cdr.markForCheck();
  }

  initFormSubscriptions() {
    this.initFormValues();

    this.onPropertyControlChange();
    this.onDynamicTypeControlChange();
    this.onCategoryControlChange();
    this.initNotifyMode();

    if (this.ticket?.id) {
      this.initEditing();
    }
  }

  private initFormValues() {
    if (this.formInitialized) return;

    if (this.data.isRepeating) {
      this.form.get('recurringPeriod').addValidators([Validators.required]);
    }

    this.form.patchValue({
      propertyId: this.ticket?.propertyId ?? null,
      bulkUnitIds: this.ticket?.bulkUnitIds ?? [],
      recurringPeriod: this.ticket?.recurringPeriod ?? null,

      id: this.ticket?.id ?? 0,
      title: this.ticket?.title ?? null,
      description: this.ticket?.description ?? null,
      assigneeId: this.ticket?.assigneeId ?? null,
      dueDate: getDateObjectFunction(this.ticket?.dueDate ?? new Date()),
      repeatUntil: this.ticket?.repeatUntil ? getDateObjectFunction(this.ticket.repeatUntil) : null,
      priority: this.ticket?.priority ?? PriorityType.Medium,

      notifyOnMode: this.getNotifyMode(this.ticket),
      customNotifyOnMode: this.ticket?.notifyAssignee ?? null,
      customNotifyOn: this.ticket?.notifyAssigneeOnCustom ?? null,
      remindOn: DontNotifyId,
      thenRemindOn: this.ticket?.remindAssigneeEveryNDays ?? null,
      until: this.ticket?.remindAssigneeStopAfterNReminders ?? null,

      dynamicTicketType: this.ticket?.dynamicTicketType ?? null,
      origin: this.ticket?.defaultOrigin ?? null,
      ticketCategoryId: this.ticket?.defaultTopicCategoryId ?? null,
      ticketTopicId: this.ticket?.defaultTopicId ?? null,
      appointmentType: this.ticket?.appointmentType ?? AppointmentTypeEnum.Unknown,
      accessNotes: this.ticket?.accessNotes ?? '',
    });

    this.calculateRecurrenceOptions(this.form.get('dueDate').value);
    this.formInitialized = true;
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private getNotifyMode(ticket?: RestBulkRepeatingTicket): number | null {
    if (!ticket?.notifyAssignee) {
      return DontNotifyId;
    }
    if (
      ticket.notifyAssignee === DynamicTicketTypeNotifyAssigneeMode.OnDueDate ||
      ticket.notifyAssignee === DynamicTicketTypeNotifyAssigneeMode.OnTicketCreation
    ) {
      return CustomNotifyId;
    }
    return DontNotifyId;
  }

  private initEditing() {
    const type = this.form.get('dynamicTicketType').value;
    const selectedType = this.dynamicTicketTypes.find(t => t.id === type);
    this.notifyOptions = this.createNotifyOptions();

    const propertyId = this.form.get('propertyId').value;
    const isPmsProperty = this.isPmsProperty(propertyId);
    this.pmsType = this.propertiesList?.find(p => p.id === propertyId)?.pmsType;

    this.store.dispatch(loadUnitList({ propertyId }));
    this.loadPropertyUsers(propertyId);

    if (selectedType?.builtInType === BuiltInTicketType.WorkOrder && isPmsProperty) {
      this.isWorkOrder = true;
      this.getPmsSettings(true);
    }

    this.cdr.detectChanges();
  }

  private initNotifyMode(): void {
    let prevNotifyValue = this.form.controls['notifyOnMode'].value;

    this.form.controls['notifyOnMode'].valueChanges
      .pipe(distinctUntilChanged(), debounceTime(100))
      .subscribe(notifyOnMode => {
        // const selectedAssignee = this.form.get('assigneeId').value;
        // if (notifyOnMode === CustomNotifyId && !selectedAssignee) {
        //   this.snackbarService.warning('We failed to read your mind. Who should we notify?');
        //   this.form.patchValue({
        //     notifyOnMode: DontNotifyId,
        //   });
        //   return;
        // }

        if (this.dialogTimeout && notifyOnMode !== CustomNotifyId) {
          clearTimeout(this.dialogTimeout);
          prevNotifyValue = notifyOnMode;
          this.isNotifyDialogOpen = false;
          return;
        }
        if (notifyOnMode === CustomNotifyId) {
          this.openCustomNotificationsDialog(prevNotifyValue, 200);
        }
        prevNotifyValue = notifyOnMode;
      })
      .untilDestroyed(this);
  }

  private loadPropertyUsers(propertyId: number) {
    this.store.dispatch(loadPropertyUsers({ propertyId }));
  }

  cancel() {
    this.dialogRef.close();
  }

  onSubmit() {
    if (this.formGroupDirective) {
      this.formGroupDirective.onSubmit(new Event('submit'));
    }
  }

  save(): void {
    if (!this.ticketService.checkRepeatingTicketPermission(this.userData, this.ticket, true)) {
      return;
    }

    this.formSubmitted = true;
    const isWorkOrderCorrect = this.isWorkOrderValuesCorrect();
    if (this.form.invalid || !isWorkOrderCorrect) {
      this.form.markAllAsTouched();
      this.cdr.detectChanges();
      return;
    }

    this.updateInProgress$.next(true);
    const formValue = this.form.value;
    if (typeof formValue.appointmentType !== 'number') formValue.appointmentType = undefined;

    const selectedType = this.dynamicTicketTypes?.find(t => t.id === formValue.dynamicTicketType)?.builtInType;

    const bulkTicket: RestBulkRepeatingTicket = {
      propertyId: formValue.propertyId,
      bulkUnitIds: formValue.bulkUnitIds,
      dynamicTicketType: formValue.dynamicTicketType,
      recurringPeriod: formValue.recurringPeriod || null,

      dueDate: formValue.dueDate,
      repeatUntil: formValue.repeatUntil ?? null,
      title: formValue.title,
      description: formValue.description,
      assigneeId: formValue.assigneeId,
      priority: formValue.priority,
      accessNotes: formValue.accessNotes,
      appointmentType: formValue.appointmentType,

      defaultOrigin: selectedType === BuiltInTicketType.WorkOrder && formValue.origin ? formValue.origin : null,
      defaultTopicCategoryId:
        selectedType === BuiltInTicketType.WorkOrder && formValue.ticketCategoryId ? formValue.ticketCategoryId : null,
      defaultTopicId:
        selectedType === BuiltInTicketType.WorkOrder && formValue.ticketTopicId ? formValue.ticketTopicId : null,
      ...this.getNotifySettings(),
    };

    if (this.ticket?.id) {
      bulkTicket.id = this.ticket.id;

      this.ticketService
        .editBulkRepeatingTicket(bulkTicket)
        .subscribe({
          next: ticket => {
            this.updateInProgress$.next(false);
            this.dialogRef.close({ result: DialogResult.Success, newTicket: ticket });
          },
          error: () => {
            this.snackbarService.error(SnackbarErrorMessage.CreatingTicket);
            this.updateInProgress$.next(false);
          },
        })
        .untilDestroyed(this);

      return;
    }

    this.ticketService
      .addBulkRepeatingTicket(bulkTicket)
      .subscribe({
        next: ticket => {
          this.updateInProgress$.next(false);
          this.dialogRef.close({ result: DialogResult.Success, newTicket: ticket });
        },
        error: () => {
          this.snackbarService.error(SnackbarErrorMessage.CreatingTicket);
          this.updateInProgress$.next(false);
        },
      })
      .untilDestroyed(this);
  }

  private getNotifySettings(): {
    notifyAssignee: DynamicTicketTypeNotifyAssigneeMode | null;
    notifyAssigneeOnCustom: number | null;
    remindAssigneeEveryNDays: number | null;
    remindAssigneeStopAfterNReminders: number | null;
  } {
    const { notifyOnMode, customNotifyOnMode, customNotifyOn, thenRemindOn, until } = this.form.value;

    if (notifyOnMode === CustomNotifyId) {
      return {
        notifyAssignee: customNotifyOnMode,
        notifyAssigneeOnCustom: customNotifyOn ?? null,
        remindAssigneeEveryNDays: thenRemindOn ?? null,
        remindAssigneeStopAfterNReminders: until ?? null,
      };
    }
    if (notifyOnMode === DontNotifyId) {
      return {
        notifyAssignee: DynamicTicketTypeNotifyAssigneeMode.DontNotify,
        notifyAssigneeOnCustom: null,
        remindAssigneeEveryNDays: null,
        remindAssigneeStopAfterNReminders: null,
      };
    }
    return {
      notifyAssignee: null,
      notifyAssigneeOnCustom: null,
      remindAssigneeEveryNDays: null,
      remindAssigneeStopAfterNReminders: null,
    };
  }

  private isWorkOrderValuesCorrect(): boolean {
    const formValue = this.form.value;
    const selectedType = this.dynamicTicketTypes?.find(t => t.id === formValue.dynamicTicketType)?.builtInType;
    if (selectedType !== BuiltInTicketType.WorkOrder) {
      return true;
    }

    let result = true;
    if (!formValue.origin && !this.defaultOrigin) {
      result = false;
      this.form.get('origin').addValidators(Validators.required);
      this.form.get('origin').updateValueAndValidity();
    }

    if (!formValue.ticketCategoryId && !this.defaultCategoryId) {
      result = false;
      this.form.get('ticketCategoryId').addValidators(Validators.required);
      this.form.get('ticketCategoryId').updateValueAndValidity();
    }

    if (!formValue.ticketTopicId && !this.defaultTopicId) {
      result = false;
      this.form.get('ticketTopicId').addValidators(Validators.required);
      this.form.get('ticketTopicId').updateValueAndValidity();
    }

    if (result) {
      this.form.get('origin').removeValidators(Validators.required);
      this.form.get('origin').updateValueAndValidity();
      this.form.get('ticketCategoryId').removeValidators(Validators.required);
      this.form.get('ticketCategoryId').updateValueAndValidity();
      this.form.get('ticketTopicId').removeValidators(Validators.required);
      this.form.get('ticketTopicId').updateValueAndValidity();
    }

    return result;
  }

  private getProperties(): Observable<IRadioButtonOption<number>[]> {
    return this.store.select(selectProperties).pipe(
      filter((properties: RestPropertyModel[]) => !!properties),
      withLatestFrom(this.store.select(selectSelectedPortfolioId)),
      map(([properties, portfolioId]) => {
        this.propertiesList = properties;
        const options = getPortfolioProperties(
          properties.filter(p => portfolioId === allPortfoliosValue || p.portfolioId === portfolioId)
        );
        return options;
      }),
      take(1)
    );
  }

  private onPropertyControlChange(): void {
    this.form
      .get('propertyId')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe(propertyId => {
        this.form.get('bulkUnitIds').setValue(null);
        this.form.get('assigneeId').setValue(null);
        this.form.get('origin').setValue(null);
        this.form.get('ticketCategoryId').setValue(null);
        this.form.get('ticketTopicId').setValue(null);
        if (propertyId) {
          this.store.dispatch(loadUnitList({ propertyId }));
          this.loadPropertyUsers(propertyId);
          this.getPmsSettings();
          this.pmsType = this.propertiesList?.find(p => p.id === propertyId)?.pmsType;
          if (this.isWorkOrder) this.fetchCategories(propertyId);
        } else {
          this.selectedPropertyId = null;
          this.users$.next([]);
          this.categories = [];
        }

        if (this.form.get('dynamicTicketType').value && this.isWorkOrder) {
          const selectedType = this.dynamicTicketTypes.find(t => t.id === this.form.get('dynamicTicketType').value);
          const isPmsProperty = this.isPmsProperty(propertyId);
          if (selectedType?.builtInType === BuiltInTicketType.WorkOrder && propertyId && !isPmsProperty) {
            this.snackbarService.error('PMS is required to create Work Order ticket');
            this.form.get('dynamicTicketType').setValue(null);
            this.isWorkOrder = false;
          }
        }
        this.cdr.markForCheck();
      })
      .untilDestroyed(this);
  }

  private onDynamicTypeControlChange(): void {
    this.form
      .get('dynamicTicketType')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe(type => {
        this.form.get('origin').setValue(null);
        this.form.get('ticketCategoryId').setValue(null);
        this.form.get('ticketTopicId').setValue(null);
        if (!type) {
          this.isWorkOrder = false;
          this.form.get('notifyOnMode').setValue(null);
          this.cdr.markForCheck();
          return;
        }

        const selectedType = this.dynamicTicketTypes.find(t => t.id === type);
        this.setNotificationFromType(selectedType);
        const propertyId = this.form.get('propertyId').value;
        const isPmsProperty = this.isPmsProperty(propertyId);
        this.isWorkOrder = selectedType?.builtInType === BuiltInTicketType.WorkOrder;
        if (selectedType?.builtInType === BuiltInTicketType.WorkOrder && propertyId && !isPmsProperty) {
          this.snackbarService.error('PMS is required to create Work Order ticket');
          this.isWorkOrder = false;
          this.form.get('dynamicTicketType').setValue(null);
        }
        if (selectedType?.builtInType === BuiltInTicketType.WorkOrder && isPmsProperty) {
          this.getPmsSettings(true);
        }
        this.cdr.markForCheck();
      })
      .untilDestroyed(this);
  }

  private setNotificationFromType(type: DynamicTicketType): void {
    if (!type) return;
    if (isExist(type.notifyAssignee)) {
      this.form.get('notifyOnMode').setValue(CustomNotifyId, { emitEvent: false });
      this.form.get('customNotifyOnMode').setValue(type.notifyAssignee, { emitEvent: false });

      isExist(type.notifyAssigneeOnCustom) &&
        this.form.get('customNotifyOn').setValue(type.notifyAssigneeOnCustom, { emitEvent: false });
    } else {
      this.form.get('notifyOnMode').setValue(DontNotifyId, { emitEvent: false });
      this.form.get('customNotifyOn').setValue(null, { emitEvent: false });
      this.form.get('thenRemindOn').setValue(null, { emitEvent: false });
      this.form.get('until').setValue(null, { emitEvent: false });
      return;
    }

    if (isExist(type.remindAssigneeEveryNDays)) {
      this.form.get('thenRemindOn').setValue(type.remindAssigneeEveryNDays, { emitEvent: false });
    } else if (this.form.get('thenRemindOn')?.value) {
      this.form.get('thenRemindOn').setValue(null, { emitEvent: false });
    }

    if (isExist(type.remindAssigneeStopAfterNReminders)) {
      this.form.get('until').setValue(type.remindAssigneeStopAfterNReminders, { emitEvent: false });
    } else if (this.form.get('until')?.value) {
      this.form.get('until').setValue(null, { emitEvent: false });
    }
  }

  private onCategoryControlChange() {
    this.form.controls['ticketCategoryId'].valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(categoryId => {
        const selectedCategory = this.categories.find(category => category.value === categoryId);
        this.topics = selectedCategory?.topics?.length
          ? selectedCategory.topics.filter(c => this.availableTopics?.includes(c.value))
          : [];
        this.form.get('ticketTopicId').setValue(null);
        this.cdr.detectChanges();
      })
      .untilDestroyed(this);
  }

  private isPmsProperty(propertyId: number): boolean {
    const property = this.propertiesList.find(p => p.id === propertyId);
    return !!property?.apiCredentialsId || !!property?.pmsType;
  }

  private getUnits(): void {
    this.store
      .select(selectUnitList)
      .pipe(
        map(unitList => {
          return unitList
            .map<IRadioButtonOption<number>>(item => {
              return {
                value: item.id,
                label: item.name,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
        })
      )
      .subscribe(units => {
        if (units?.length === 1) this.form.get('bulkUnitIds').setValue([units[0].value]);
        this.units$.next(units);
      })
      .untilDestroyed(this);
  }

  private getDynamicTicketTypes(): Observable<IRadioButtonOption<number>[]> {
    return this.ticketTypeService.getDynamicTicketTypes().pipe(
      take(1),
      tap(res => {
        if (!this.dynamicTicketTypes?.length) {
          this.dynamicTicketTypes = res;
        }
        this.isTicketTypesLoading = false;
        this.cdr.markForCheck();
      }),
      map(res => res.map(t => ({ value: t.id, label: t.name })))
    );
  }

  private getPmsSettings(init = false): void {
    const dynamicTicketTypeId = this.form.get('dynamicTicketType').value;
    const propertyId = this.form.get('propertyId').value;
    if (
      dynamicTicketTypeId &&
      propertyId &&
      (this.selectedTypeId !== dynamicTicketTypeId || this.selectedPropertyId !== propertyId)
    ) {
      this.selectedTypeId = dynamicTicketTypeId;
      this.selectedPropertyId = propertyId;
      this.isSettingsLoading = true;
      this.cdr.detectChanges();
      this.ticketTypeService
        .getDynamicTicketTypePmsSettingsListByProperty(dynamicTicketTypeId, propertyId)
        .pipe(
          finalize(() => {
            this.isSettingsLoading = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe(res => {
          if (res[0]?.availableOriginsToSelectByUser?.length) {
            this.originOptions = res[0].availableOriginsToSelectByUser.map(o => ({
              value: o,
              label: o,
            }));
          } else {
            this.originOptions = [];
          }

          if (res[0]?.availableCategoriesToSelectByUser?.length) {
            this.availableCategories = res[0].availableCategoriesToSelectByUser;
            this.categories = this.categories?.filter(c => this.availableCategories.includes(c.value)) ?? [];
          } else {
            this.availableCategories = [];
            this.categories = [];
          }

          if (res[0]?.availableTopicsToSelectByUser?.length) {
            this.availableTopics = res[0].availableTopicsToSelectByUser;
          } else {
            this.availableTopics = [];
          }

          this.defaultCategoryId = res[0]?.defaultCategoryId || null;
          this.defaultTopicId = res[0]?.defaultTopicId || null;
          this.defaultOrigin = res[0]?.defaultOrigin || null;

          if (init) {
            this.fetchCategories(propertyId, true);
          }
          this.cdr.detectChanges();
        })
        .untilDestroyed(this);
    }
  }

  private fetchCategories(propertyId: number, initTopics = false) {
    this.isCategoriesLoading = true;
    this.cdr.detectChanges();

    this.ticketService
      .getCashedCategories(propertyId)
      .pipe(
        take(1),
        finalize(() => {
          this.isCategoriesLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(data => {
        this.categories = data
          .map(category => ({
            value: category.id,
            label: category.name,
            topics: category.topics.map(topic => ({
              value: topic.id,
              label: topic.name,
            })),
          }))
          .filter(c => this.availableCategories?.includes(c.value));

        if (initTopics) {
          const selectedCategory = this.form.get('ticketCategoryId').value;
          const category = this.categories.find(c => c.value === selectedCategory);
          this.topics = category?.topics ? category.topics.filter(c => this.availableTopics?.includes(c.value)) : [];
        }
      })
      .untilDestroyed(this);
  }

  private calculateRecurrenceOptions(dueDate: Date) {
    this.recurrenceOptions = GetRecurrencePeriodTypeList(dueDate);
    this.cdr.detectChanges();
  }

  private createNotifyOptions(): IRadioButtonOption<number>[] {
    return [DontNotifyOption, CustomNotifyOption];
  }

  openCustomNotificationsDialog(prevValue?: number, delay = 0): void {
    if (this.isNotifyDialogOpen) {
      return;
    }
    this.isNotifyDialogOpen = true;
    this.dialogTimeout = setTimeout(() => {
      this.dialog
        .open(TicketCustomNotificationDialogComponent, {
          panelClass: 'modal-container-without-padding',
          width: '384px',
          autoFocus: false,
          data: {
            dueDate: this.form.get('dueDate').value,
            customNotifyOnMode: this.form.get('customNotifyOnMode').value,
            notifyOn: this.form.get('customNotifyOn').value,
            thenRemindOn: this.form.get('thenRemindOn').value,
            until: this.form.get('until').value,
          },
        })
        .afterClosed()
        .pipe(
          take(1),
          finalize(() => (this.isNotifyDialogOpen = false))
        )
        .subscribe(res => {
          if (!res) {
            isExist(prevValue) && this.form.get('notifyOnMode').setValue(prevValue);
            return;
          }

          if (isExist(res.customNotifyOnMode)) {
            this.form.get('customNotifyOnMode').setValue(res.customNotifyOnMode);
          }

          if (isExist(res.notifyOn)) {
            const value = res.notifyOn === DontNotifyId ? null : res.notifyOn;
            this.form.get('customNotifyOn').setValue(value);
            value === null && this.form.get('notifyOnMode').setValue(DontNotifyId);
            this.form.get('notifyOnMode').markAsDirty();
          }
          // if (isExist(res.remindOn)) {
          //   const value = res.remindOn === DontNotifyId ? null : res.remindOn;
          //   this.form.get('remindOn').setValue(value);
          //   this.form.get('remindOn').markAsTouched();
          // }
          if (isExist(res.thenRemindOn)) {
            const value = res.thenRemindOn === DontNotifyId ? null : res.thenRemindOn;
            this.form.get('thenRemindOn').setValue(value);
            this.form.get('thenRemindOn').markAsTouched();
          }
          if (isExist(res.until)) {
            const value = res.until === CompletedId ? null : res.until;
            this.form.get('until').setValue(value);
            this.form.get('until').markAsTouched();
          }
          this.cdr.detectChanges();
        })
        .untilDestroyed(this);
    }, delay);
  }
}
